import { useTranslation } from 'react-i18next';
import {
  HubContext,
  Submissions,
  mapQuotesToSubmissionsForPrincipals,
  transformHubContextJobToJob,
  transformPrincipalQuoteToJobQuote,
} from '../../types/hub';
import { useState } from 'react';
import CollapsibleContent from './CollapsibleContent';
import SubmissionListHeader from './SubmissionListHeader';
import BidderDetailsModal from '../quotes/BidderDetailsModal';
import { JobQuote } from '../../types/job';
import ScrollableContainer from './ScrollableContainer';
import { SubmissionRow } from '../hub/SubmissionRow';

interface SubmissionListProps {
  hub: HubContext;
}

export default function SubmissionList({ hub }: SubmissionListProps) {
  const submissions: Submissions = mapQuotesToSubmissionsForPrincipals(hub.job.quotes);

  const { t } = useTranslation('hub');

  const [expanded, setExpanded] = useState(false);

  const [selectedQuote, setSelectedQuote] = useState<JobQuote | null>(null);

  const transformedJob = transformHubContextJobToJob(hub.job);
  const transformedQuotes = hub.job.quotes ? transformPrincipalQuoteToJobQuote(hub.job.quotes) : [];

  const handleSelectQuote = (quoteId: string) => {
    const quote = transformedQuotes.find(q => q.id === quoteId);
    if (quote) {
      setSelectedQuote(quote);
    }
  };

  return (
    <>
      {hub.job.quotes && submissions.length > 0 ? (
        <>
          <div className="mb-2 font-semibold ml-2">
            {t('submission_tracking', { ns: 'hub' }).toLocaleUpperCase()}
          </div>
          <div className="bg-white rounded-2xl p-6 border-2 border-gray-300 shadow-xl">
            <CollapsibleContent
              className="w-full flex flex-col transition-all duration-300"
              header={
                <SubmissionListHeader
                  hub={hub}
                  submissions={submissions}
                  onSelectQuote={handleSelectQuote}
                  expanded={expanded}
                />
              }
              defaultExpanded={true}
              headerClassName="relative items-center"
              expandButtonClassName="absolute self-start"
              onToggle={setExpanded}
            >
              <ScrollableContainer>
                <div className="min-w-[740px] divide-y divide-gray-400 border-t border-gray-400">
                  {/* MOBILE : ALL */}
                  <div className="md:hidden">
                    {submissions.map(item => (
                      <SubmissionRow
                        key={item.id}
                        item={item}
                        hub={hub}
                        onSelectQuote={handleSelectQuote}
                      />
                    ))}
                  </div>

                  {/* DESKTOP */}
                  <div className="hidden md:block">
                    {submissions.slice(1).map(item => (
                      <SubmissionRow
                        key={item.id}
                        item={item}
                        hub={hub}
                        onSelectQuote={handleSelectQuote}
                      />
                    ))}
                  </div>
                </div>
              </ScrollableContainer>
            </CollapsibleContent>
          </div>

          {selectedQuote && (
            <BidderDetailsModal
              job={transformedJob}
              quote={selectedQuote}
              onClose={() => setSelectedQuote(null)}
            />
          )}
        </>
      ) : (
        <p className="mb-4 text-xl text-gray-400 ml-2">
          {t('noSubmissionTracking', { ns: 'hub' })}
        </p>
      )}
    </>
  );
}
